<template>
  <section class="py-6 bg-white w-full">
    <div class="mx-4">
      <div class="relative" 
           @touchstart="handleTouchStart" 
           @touchmove="handleTouchMove" 
           @touchend="handleTouchEnd">
        <!-- 图片网格 -->
        <div class="grid gap-3 mb-4 transition-all duration-500 ease-out" :class="gridClass">
          <div v-for="(item, index) in paginatedImages" :key="`${currentPage}-${index}`" 
               class="relative animate-slide-up"
               :style="{ animationDelay: `${index * 0.1}s` }">
            <div class="relative w-full" style="aspect-ratio: 5712 / 4284;">
              <!-- 占位图 -->
              <div v-if="!imageLoadedStates[`${currentPage}-${index}`]" 
                   class="absolute inset-0 bg-gray-200 rounded-lg flex items-center justify-center">
                <div class="text-gray-500 text-center font-medium">
                  <div class="text-lg font-bold">Creston Group</div>
                </div>
              </div>
              
              <!-- 实际图片 -->
              <img :src="item.src" :alt="item.alt" 
                   class="absolute inset-0 w-full h-full object-cover rounded-lg cursor-pointer transition-transform duration-200 hover:scale-105"
                   :class="{ 'opacity-0': !imageLoadedStates[`${currentPage}-${index}`] }"
                   @load="handleImageLoad(`${currentPage}-${index}`)"
                   @click="showImageOverlay(item)" />
            </div>
            
            <div v-if="item.caption"
                 class="absolute bottom-0 left-0 right-0 p-2 font-medium bg-black bg-opacity-50 text-white text-xs leading-[1.13]">
              {{ item.caption }}
            </div>
          </div>
        </div>
        
        <!-- 页码指示器 -->
        <div class="flex justify-center space-x-2 mb-4" v-if="totalPages > 1">
          <div v-for="page in totalPages" :key="page" 
               class="w-2 h-2 rounded-full"
               :class="page === currentPage ? 'bg-blue-900' : 'bg-gray-300'">
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端大图预览 -->
    <div v-if="hoveredImage" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
      @click.self="closeImageOverlay">
      <div class="relative max-w-[90vw] max-h-[80vh] flex items-center justify-center">
        <img :src="hoveredImage.src" :alt="hoveredImage.alt"
          class="max-w-full max-h-full object-contain rounded shadow-lg" />
        <button @click="closeImageOverlay"
          class="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center">
          ✕
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

interface ImageItem {
  src: string;
  alt: string;
  caption: string;
}

const props = defineProps<{
  imageItems: ImageItem[];
  imagesPerPage?: number;
  columns?: number;
  aspectRatio?: string;
}>();

const gridClass = computed(() => {
  const col = props.columns || 2;
  return {
    'grid-cols-1': col === 1,
    'grid-cols-2': col === 2,
    'grid-cols-3': col === 3,
  };
});

// 分页相关
const currentPage = ref(1);
const imagesPerPage = computed(() => {
  // 如果是1列布局，每页只显示1张图片
  if (props.columns === 1) {
    return 1;
  }
  return props.imagesPerPage || 8;
});

// 触摸事件相关
const touchStartX = ref(0);
const touchEndX = ref(0);

// 计算总页数
const totalPages = computed(() => Math.ceil(props.imageItems.length / imagesPerPage.value));

// 当前页的图片
const paginatedImages = computed(() => {
  const startIndex = (currentPage.value - 1) * imagesPerPage.value;
  return props.imageItems.slice(startIndex, startIndex + imagesPerPage.value);
});

const hoveredImageIndex = ref<number | null>(null);
const hoveredImage = computed(() => hoveredImageIndex.value !== null ? props.imageItems[hoveredImageIndex.value] : null);

// 翻页函数
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// 触摸事件处理
const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX;
};

const handleTouchMove = (e: TouchEvent) => {
  touchEndX.value = e.touches[0].clientX;
};

const handleTouchEnd = () => {
  const swipeDistance = touchStartX.value - touchEndX.value;
  const minSwipeDistance = 50;

  if (Math.abs(swipeDistance) > minSwipeDistance) {
    if (swipeDistance > 0) {
      nextPage();
    } else {
      prevPage();
    }
  }
};

const showImageOverlay = (image: ImageItem) => {
  const index = props.imageItems.findIndex(i => i.src === image.src);
  if (index !== -1) hoveredImageIndex.value = index;
};

const closeImageOverlay = () => {
  hoveredImageIndex.value = null;
};

// 图片加载状态管理
const imageLoadedStates = ref<Record<string, boolean>>({});

const handleImageLoad = (key: string) => {
  imageLoadedStates.value[key] = true;
};

// 监听当前页变化，预设加载状态
watch(currentPage, () => {
  // 为当前页的图片初始化加载状态
  paginatedImages.value.forEach((_, index) => {
    const key = `${currentPage.value}-${index}`;
    if (!(key in imageLoadedStates.value)) {
      imageLoadedStates.value[key] = false;
    }
  });
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.4s ease-out;
}

/* 页码指示器动画 */
.w-2.h-2.rounded-full {
  transition: all 0.3s ease-in-out;
}

.w-2.h-2.rounded-full:hover {
  transform: scale(1.2);
}
</style>
